// src/schemas/checkout.schema.ts
import { UseFormReturn } from "react-hook-form";
import { z } from "zod";

/* Paso 2 – Datos del comprador */
export const TIPO_DOCUMENTO_VALUES = ['dni', 'cuit', 'pasaporte'] as const;
export type TipoDocumento = typeof TIPO_DOCUMENTO_VALUES[number];

export const datosCompradorSchema = z.object({
  nombre: z
    .string()
    .min(1, "Nombre es obligatorio")
    .min(3, "Debe tener al menos 3 caracteres")
    .max(60, "Máx. 60 caracteres"),
  email: z
    .string()
    .min(1, "Email es obligatorio")
    .email("Formato de email inválido (ej: usuario@ejemplo.com)")
    .max(120, "Email muy largo"),
  telefono: z
    .string()
    .min(1, "Teléfono es obligatorio")
    .regex(/^\d{6,15}$/, "Solo números, entre 6 y 15 dígitos"),
  tipoDocumento: z
    .string()
    .min(1, "Seleccioná un tipo de documento")
    .refine((val) => TIPO_DOCUMENTO_VALUES.includes(val as TipoDocumento), {
      message: "Tipo de documento inválido (DNI, CUIT o Pasaporte)",
    }),
  doc: z
    .string()
    .min(1, "Documento es obligatorio"),
}).superRefine((data, ctx) => {
  const patterns: Record<TipoDocumento, { regex: RegExp; message: string }> = {
    dni:       { regex: /^\d{7,8}$/,           message: "DNI: ingresá 7 u 8 dígitos" },
    cuit:      { regex: /^\d{11}$/,            message: "CUIT: ingresá 11 dígitos sin guiones" },
    pasaporte: { regex: /^[A-Za-z0-9]{6,20}$/, message: "Pasaporte: 6-20 caracteres alfanuméricos" },
  };
  const rule = patterns[data.tipoDocumento as TipoDocumento];
  if (rule && !rule.regex.test(data.doc)) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, message: rule.message, path: ["doc"] });
  }
});

/* Paso 3 – Tarjeta (Mercado Pago) */
export const tarjetaSchema = z.object({
  nombre: z
    .string()
    .min(3, "Igual que en la tarjeta")
    .max(60),
  numero: z
    .string()
    .regex(/^\d{13,19}$/, "Debe tener entre 13 y 19 dígitos"),
  vencimiento: z
    .string()
    .regex(/^(0[1-9]|1[0-2])\/\d{2}$/, "Formato MM/AA"),
  cvv: z
    .string()
    .regex(/^\d{3,4}$/, "3 ó 4 dígitos"),
  doc: z
    .string()
    .regex(/^[A-Za-z0-9]{6,20}$/, "Sin puntos ni guiones. DNI: 7-8 dígitos · CUIT: 11 dígitos · Pasaporte: letras y números"),
});

/* Paso 1 – Entradas seleccionadas */
export const entradaSeleccionadaSchema = z.object({
  tipoEntradaId: z.string(),
  cantidad: z.number().int().positive().max(20),
  nombre: z.string(),
  precio: z.number().positive(),
  descripcion: z.string().optional(),
  asientos: z.array(z.object({ seatId: z.string(), mesaId: z.string() })),
});
export const carritoSchema = z.array(entradaSeleccionadaSchema).min(1);

export const GATEWAY_VALUES = ['mercadopago', 'allaria'] as const;
export type Gateway = typeof GATEWAY_VALUES[number];

/* Schema total para “Confirmar y Pagar” */
export const checkoutSchema = z.object({
  eventoId: z.string(),
  funcionId: z.string(),
  entradas: carritoSchema,
  comprador: datosCompradorSchema,
  tarjeta: tarjetaSchema.optional(),
  eventId: z.string().optional(),
  gateway: z.enum(GATEWAY_VALUES).optional(),
});

/* 👉 TypeScript helpers */
export type DatosComprador = z.infer<typeof datosCompradorSchema>;
export type TarjetaForm = z.infer<typeof tarjetaSchema>;
export type EntradaSeleccionada = z.infer<typeof entradaSeleccionadaSchema>;
export type CheckoutPayload = z.infer<typeof checkoutSchema>;

export type CheckoutForm = UseFormReturn<CheckoutPayload>
export type CompradorForm = UseFormReturn<CheckoutPayload>
